import { PropType, computed, defineComponent, onUnmounted, ref, watch } from 'vue'
import { Store } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import { PowerResource } from '../iconRender/icon.model'
import { gcj02ToBd09 } from '@/utils/coordinateTransform'
import { FilterItem, FilterKey, FilterState } from '../filter/filter.model'
import { showAllResourceTypes } from '@/pages/chargermap/utils/resource'
import { RouteCDNModel } from '../../models/detail.model'
import { calcSize, drawPointCountry, resizeMapResources } from '../chargerMap/mapUtils'
import { DEFAULT_DISTRICT_ZOOM_LEVEL } from '../../config/zoomLevelConfig'
import { ChargeMapContext } from '../chargerMap/index.model'
import { ComponentActions, ComponentState } from '../../nsh5/localStores/component'
import { MapViewMode, Point } from '../../contract/map'

/**
 * 全国小点渲染功能模块
 */
export default defineComponent({
  name: 'dot-render-view',
  props: {
    mapContextRef: {
      type: Object as PropType<ChargeMapContext>,
    },
    mapInstance: {
      type: Object,
    },
    componentStore: {
      // 用于同一项目功能模块间的通信
      type: Object as PropType<Store<string, ComponentState, {}, ComponentActions>>,
      default: null,
    },
    cdnUrl: {
      type: RouteCDNModel,
      default: null,
    },
  },

  setup: (props) => {
    const mapContextRef = computed(() => props.mapContextRef)
    const mapInstance = computed(() => props.mapInstance)
    const zoomLevel = ref<number>(0)
    const route = useRoute()
    let resourceFilter: FilterItem[] = [] // 资源点
    let timer: any

    const allResources: any = {
      swap: [] as PowerResource[],
      charge: [] as PowerResource[],
      third: [] as PowerResource[],
      nioStore: [] as PowerResource[],
      testDrive: [] as PowerResource[],
      serviceCenter: [] as PowerResource[],
    }

    // 获取组件配置
    const componentsConfig: any = window.NSComponentsConfig

    /**
     * 通过cdn获取资源点
     * @param key
     * @param cdnUrlRes
     * @param display 加载完成后是否展示
     * @returns
     */
    const getResourcesByCND = (key: string, cdnUrlRes: any, display = true) => {
      allResources[key] = [] as PowerResource[]
      if (key == 'third' && !componentsConfig?.third_resource?.show) {
        // 获取是否支持全国三方显示
        return
      }
      const linkKey = key + 'ResourcesLink'
      if (cdnUrlRes[linkKey]?.length) {
        let index = 0
        return Promise.all(
          cdnUrlRes[linkKey].map((url: string) =>
            window
              .fetch(url)
              .then((response) => response.json())
              .then((json) => {
                const res = json.map((item: any) => {
                  if (item?.id) {
                    const position = item?.location.split(',')
                    const point = gcj02ToBd09({
                      lng: Number(position[0]),
                      lat: Number(position[1]),
                    })
                    return {
                      id: item?.id,
                      lng: point?.lng,
                      lat: point?.lat,
                      type: key == 'swap' ? 'PowerSwap' : item.type,
                    } as Point
                  } else {
                    const position = item?.split(',')
                    return gcj02ToBd09({
                      lng: Number(position[0]),
                      lat: Number(position[1]),
                    })
                  }
                })
                index++
                drawPointCountry(mapContextRef.value!, res as Point[], key, key + index, display)
                allResources[key].push(...(res as Point[]))
                props.componentStore.$patch({
                  allResources: allResources,
                })
                return res
              })
          )
        )
      }
    }

    /**
     * 隐藏所有资源点
     */
    const hideAllPointLayer = () => {
      mapContextRef.value?.operatePointLayer('swap', false)
      mapContextRef.value?.operatePointLayer('charge', false)
      mapContextRef.value?.operatePointLayer('third', false)
      mapContextRef.value?.operatePointLayer('nioStore', false)
      mapContextRef.value?.operatePointLayer('testDrive', false)
      mapContextRef.value?.operatePointLayer('serviceCenter', false)
    }

    const dotFilterChanged = (params: any) => {
      if (
        props.componentStore.viewMode === MapViewMode.Init &&
        zoomLevel.value <= DEFAULT_DISTRICT_ZOOM_LEVEL
      ) {
        const thirdSourceIndex = params.thirdSourceIndex
        resourceFilter =
          params.filter[thirdSourceIndex[0]]?.options[thirdSourceIndex[1]]?.options || []
        showResourcesDotByFilter()
      } else {
        hideAllPointLayer()
      }
    }

    props.componentStore.bus.on('filterChanged', dotFilterChanged)

    /**
     * 筛选资源加载完毕需要更新记录的资源信息
     */
    const dotInitResourceFilter = (params: any) => {
      const thirdSourceIndex = params.thirdSourceIndex
      resourceFilter =
        params.filter[thirdSourceIndex[0]]?.options[thirdSourceIndex[1]]?.options || []
    }
    props.componentStore.bus.on('initResourceFilter', dotInitResourceFilter)

    /**
     * 手动触发筛选资源点
     * eg 回到首页
     */
    props.componentStore.bus.on('showResourcesDotByFilter', () => {
      showResourcesDotByFilter()
    })
    /**
     * 根据筛选 展示或隐藏资源点
     * @returns
     */
    const showResourcesDotByFilter = () => {
      props.componentStore.filter.forEach((element: FilterItem) => {
        if (element.key == FilterKey.ServiceCenter) {
          mapContextRef.value?.operatePointLayer(
            'serviceCenter',
            element.filterState == FilterState.On
          )
          return
        }
        if (element.key == FilterKey.TestDrive) {
          mapContextRef.value?.operatePointLayer('testDrive', element.filterState == FilterState.On)
          return
        }
        if (element.key == FilterKey.NioStore) {
          mapContextRef.value?.operatePointLayer('nioStore', element.filterState == FilterState.On)
          return
        }
        // -------------原有recharge处理 --------------
        // if (element.key == FilterKey.Recharge) {
        //   if (element.filterState == FilterState.On) {
        //     // 路线路由下不展示蔚来门店和试驾点
        //     const specialFilterShow =
        //       resourceFilter[0]?.filterState == FilterState.Off &&
        //       resourceFilter[1]?.filterState == FilterState.Off &&
        //       resourceFilter[2]?.filterState == FilterState.Disabled
        //     // 是否展示换电站
        //     if (
        //       resourceFilter[0]?.filterState == FilterState.On ||
        //       showAllResourceTypes(resourceFilter) ||
        //       specialFilterShow
        //     ) {
        //       mapContextRef.value?.operatePointLayer('swap', true)
        //     } else {
        //       mapContextRef.value?.operatePointLayer('swap', false)
        //     }
        //     // 是否展示充电桩
        //     if (
        //       resourceFilter[1]?.filterState == FilterState.On ||
        //       showAllResourceTypes(resourceFilter) ||
        //       specialFilterShow
        //     ) {
        //       mapContextRef.value?.operatePointLayer('charge', true)
        //     } else {
        //       mapContextRef.value?.operatePointLayer('charge', false)
        //     }
        //     if (route.name === 'home') {
        //       // 是否展示三方桩
        //       if (
        //         resourceFilter[2]?.filterState == FilterState.On ||
        //         showAllResourceTypes(resourceFilter)
        //       ) {
        //         mapContextRef.value?.operatePointLayer('third', true)
        //       } else {
        //         mapContextRef.value?.operatePointLayer('third', false)
        //       }
        //     } else {
        //       // 非首页不展示三方桩
        //       mapContextRef.value?.operatePointLayer('third', false)
        //     }
        //   } else {
        //     mapContextRef.value?.operatePointLayer('swap', false)
        //     mapContextRef.value?.operatePointLayer('charge', false)
        //     mapContextRef.value?.operatePointLayer('third', false)
        //   }
        //   return
        // }
        // ---------------------

        // ----------- 20240523 兼容处理 -----
        if (element.key == FilterKey.RechargePS) {
          if (element.filterState == FilterState.On) {
            // 是否展示换电站
            mapContextRef.value?.operatePointLayer('swap', true)
          } else {
            mapContextRef.value?.operatePointLayer('swap', false)
          }
          return
        }
        if (element.key == FilterKey.RechargeCS) {
          if (element.filterState == FilterState.On) {
            // 路线路由下不展示蔚来门店和试驾点
            const specialFilterShow =
              resourceFilter[0]?.filterState == FilterState.Off &&
              resourceFilter[1]?.filterState == FilterState.Disabled
            // 是否展示充电桩
            if (
              resourceFilter[0]?.filterState == FilterState.On ||
              showAllResourceTypes([resourceFilter[0], resourceFilter[1]]) ||
              specialFilterShow
            ) {
              mapContextRef.value?.operatePointLayer('charge', true)
            } else {
              mapContextRef.value?.operatePointLayer('charge', false)
            }
            if (route.name === 'home' || route.name === 'data_summary') {
              // 20240815 首页和数据页切回全国  按条件 展示三方桩
              // 是否展示三方桩
              if (
                resourceFilter[1]?.filterState == FilterState.On ||
                showAllResourceTypes([resourceFilter[0], resourceFilter[1]])
              ) {
                mapContextRef.value?.operatePointLayer('third', true)
              } else {
                mapContextRef.value?.operatePointLayer('third', false)
              }
            } else {
              // 非首页不展示三方桩
              mapContextRef.value?.operatePointLayer('third', false)
            }
          } else {
            mapContextRef.value?.operatePointLayer('charge', false)
            mapContextRef.value?.operatePointLayer('third', false)
          }
          return
        }
      })
    }

    // 优先让地图加载和渲染完成后再请求数据
    const mapReadyRequestData = async (cdnUrlRes: any) => {
      if (componentsConfig?.filter?.properties?.default_selected_status) {
        // 读取默认显示配置
        componentsConfig?.filter?.properties?.default_selected_status.forEach(
          (element: boolean, index: number) => {
            const filterItem = componentsConfig?.filter?.properties?.items?.split(',')
            switch (filterItem[index]) {
              case 'service_center':
                getResourcesByCND(
                  'serviceCenter',
                  cdnUrlRes,
                  componentsConfig?.filter?.properties?.default_selected_status[index] &&
                    props.componentStore.viewMode == MapViewMode.Init
                ) // 获取服务中心
                break
              case 'test_drive':
                getResourcesByCND(
                  'testDrive',
                  cdnUrlRes,
                  componentsConfig?.filter?.properties?.default_selected_status[index] &&
                    props.componentStore.viewMode == MapViewMode.Init
                ) // 获取试驾点资源
                break
              case 'nio_store':
                getResourcesByCND(
                  'nioStore',
                  cdnUrlRes,
                  componentsConfig?.filter?.properties?.default_selected_status[index] &&
                    props.componentStore.viewMode == MapViewMode.Init
                ) // 获取蔚来门店资源
                break
              // --------------兼容recharge 0523之后可删除--------
              // case 'recharge':
              //   getResourcesByCND(
              //     'swap',
              //     cdnUrlRes,
              //     componentsConfig?.filter?.properties?.default_selected_status[index]
              //   ) // 获取全国换电站资源
              //   getResourcesByCND(
              //     'charge',
              //     cdnUrlRes,
              //     componentsConfig?.filter?.properties?.default_selected_status[index]
              //   ) // 获取全国充电桩资源
              //   getResourcesByCND(
              //     'third',
              //     cdnUrlRes,
              //     componentsConfig?.filter?.properties?.default_selected_status[index]
              //   ) // 获取全国三方资源
              //   break
              // --------------------
              // 0523处理
              case 'recharge|ps':
                getResourcesByCND(
                  'swap',
                  cdnUrlRes,
                  componentsConfig?.filter?.properties?.default_selected_status[index] &&
                    props.componentStore.viewMode == MapViewMode.Init
                ) // 获取全国换电站资源
                break
              case 'recharge|cs':
                getResourcesByCND(
                  'charge',
                  cdnUrlRes,
                  componentsConfig?.filter?.properties?.default_selected_status[index] &&
                    props.componentStore.viewMode == MapViewMode.Init
                ) // 获取全国充电桩资源
                getResourcesByCND(
                  'third',
                  cdnUrlRes,
                  componentsConfig?.filter?.properties?.default_selected_status[index] &&
                    props.componentStore.viewMode == MapViewMode.Init
                ) // 获取全国三方资源
                break

              default:
                break
            }
          }
        )
      } else {
        // getResourcesByCND('swap', cdnUrlRes) // 获取全国换电站资源
        // getResourcesByCND('charge', cdnUrlRes) // 获取全国充电桩资源
        // getResourcesByCND('third', cdnUrlRes) // 获取全国三方资源
        // getResourcesByCND('nioStore', cdnUrlRes) // 获取蔚来门店资源
        // getResourcesByCND('testDrive', cdnUrlRes) // 获取试驾点资源
        // getResourcesByCND('serviceCenter', cdnUrlRes) // 获取服务中心

        // 20240912 在新站上新模式下，关闭原有的
        getResourcesByCND('swap', cdnUrlRes, props.componentStore.viewMode == MapViewMode.Init) // 获取全国换电站资源
        getResourcesByCND('charge', cdnUrlRes, props.componentStore.viewMode == MapViewMode.Init) // 获取全国充电桩资源
        getResourcesByCND('third', cdnUrlRes, props.componentStore.viewMode == MapViewMode.Init) // 获取全国三方资源
        getResourcesByCND('nioStore', cdnUrlRes, props.componentStore.viewMode == MapViewMode.Init) // 获取蔚来门店资源
        getResourcesByCND('testDrive', cdnUrlRes, props.componentStore.viewMode == MapViewMode.Init) // 获取试驾点资源
        getResourcesByCND(
          'serviceCenter',
          cdnUrlRes,
          props.componentStore.viewMode == MapViewMode.Init
        ) // 获取服务中心
      }
    }

    const initDotData = () => {
      // 请求cdn资源
      timer && clearInterval(timer)
      let index = 0
      timer = setInterval(() => {
        if (props.cdnUrl) {
          clearInterval(timer)
          mapReadyRequestData(props.cdnUrl)
        } else {
          index++
        }
        if (index > 6) {
          clearInterval(timer)
        }
      }, 1000)
    }

    // ------------暴露出去的方法--------------
    const zoomEnd = async () => {
      zoomLevel.value = mapInstance.value?.getZoom()
    }

    // 启动相关事件监听
    const initEventListener = () => {
      mapInstance.value?.addEventListener('zoomend', zoomEnd, false)
    }

    // // unmounted时摧毁相关事件监听
    // const destroyEventListener = () => {
    //   mapInstance.value?.removeEventListener('zoomend', zoomEnd, false)
    // }

    onUnmounted(() => {
      // destroyEventListener()
      props.componentStore.bus.off('filterChanged', dotFilterChanged) // 很多地方都监听了这个事件，所以不能直接off，需要指明具体的监听函数, 防止重复创建
      props.componentStore.bus.off('initResourceFilter', dotInitResourceFilter) // 很多地方都监听了这个事件，所以不能直接off，需要指明具体的监听函数, 防止重复创建
      props.componentStore.bus.off('showResourcesDotByFilter')
    })

    /**
     * 组件获取地图实例后的回调
     */
    const onReady = () => {
      // mapInstance.value = mapContextRef.value?.getMap()
      initEventListener()
      initDotData()
    }
    watch(
      () => props.mapContextRef,
      () => {
        if (props.mapContextRef) {
          // mapContextRef.value = props.mapContextRef
          onReady()
        }
      },
      {
        immediate: true,
      }
    )

    watch(
      () => route.name,
      () => {
        if (
          route.name !== 'home' &&
          route.name !== 'data_summary' &&
          route.name !== 'data_chooseCity' &&
          route.name !== 'countyLayout' && // 地址选择退回到countyLayout，小点不要消失
          route.name !== 'wishpower_chooseCity'
        ) {
          // url直接进入非首页
          hideAllPointLayer()
        }
      },
      { immediate: true }
    )

    watch(
      () => props.componentStore.viewMode,
      () => {
        if (
          props.componentStore.viewMode === MapViewMode.Init &&
          zoomLevel.value <= DEFAULT_DISTRICT_ZOOM_LEVEL
        ) {
          showResourcesDotByFilter()
        } else if (props.componentStore.viewMode === MapViewMode.WishPower) {
          mapContextRef.value?.operatePointLayer('swap', true)
        } else {
          hideAllPointLayer()
        }
      }
    )

    watch(
      () => zoomLevel.value,
      (newVal) => {
        if (props.componentStore.viewMode === MapViewMode.Init) {
          if (newVal > DEFAULT_DISTRICT_ZOOM_LEVEL) {
            // 放大消失
            hideAllPointLayer()
          } else if (newVal <= DEFAULT_DISTRICT_ZOOM_LEVEL) {
            // 缩小显示
            showResourcesDotByFilter()
          }
        } else if (props.componentStore.viewMode === MapViewMode.WishPower) {
          // 心愿站小点显示或隐藏   到市才隐藏
          mapContextRef.value?.operatePointLayer('swap', newVal <= DEFAULT_DISTRICT_ZOOM_LEVEL)
        }
        resizeMapResources(mapContextRef.value!, calcSize(zoomLevel.value).w) // 调整点的大小
        // if (props.componentStore.viewMode === MapViewMode.CountyLayout) {
        //   resizeMapResources(mapContextRef.value!, calcSizeCountyLayout(zoomLevel.value).w) // 调整点的大小
        // } else {
        //   resizeMapResources(mapContextRef.value!, calcSize(zoomLevel.value).w) // 调整点的大小
        // }
      }
    )
    return () => {}
  },
})
